import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { AutoTranslateDirective } from 'app/shared/directives/auto-translate.directive';

@Component({
    selector: 'prehox-paginator',
    standalone: true,
    imports: [CommonModule, MatButtonModule, MatIconModule, AutoTranslateDirective],
    templateUrl: './prehox-paginator.component.html',
    styleUrls: ['./prehox-paginator.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'prehox-paginator',
    },
})
export class PrehoxPaginatorComponent {
    @Input() length = 0;
    @Input() pageIndex = 0;
    @Input() pageSize = 10;
    @Input() pageSizeOptions: number[] = [5, 10, 25, 100];
    @Input() disabled = false;
    @Input() hidePageSize = false;
    @Input() showFirstLastButtons = false;

    @Output() page = new EventEmitter<PageEvent>();

    get totalPages(): number {
        if (this.pageSize <= 0) {
            return 0;
        }

        return Math.max(Math.ceil(this.length / this.pageSize), 0);
    }

    get visiblePages(): number[] {
        const total = this.totalPages;
        if (total === 0) {
            return [];
        }

        const maxVisible = 5;
        if (total <= maxVisible) {
            return Array.from({ length: total }, (_, index) => index);
        }

        let start = Math.max(0, this.pageIndex - 2);
        let end = start + maxVisible;

        if (end > total) {
            end = total;
            start = end - maxVisible;
        }

        return Array.from({ length: end - start }, (_, index) => start + index);
    }

    get hasPreviousPage(): boolean {
        return this.pageIndex > 0;
    }

    get hasNextPage(): boolean {
        return this.pageIndex < this.totalPages - 1;
    }

    firstPage(): void {
        this.goToPage(0);
    }

    goToPage(index: number): void {
        if (this.disabled || index < 0 || index >= this.totalPages || index === this.pageIndex) {
            return;
        }

        this.emitPage(index, this.pageSize);
    }

    previousPage(): void {
        this.goToPage(this.pageIndex - 1);
    }

    nextPage(): void {
        this.goToPage(this.pageIndex + 1);
    }

    lastPage(): void {
        this.goToPage(this.totalPages - 1);
    }

    onPageSizeChange(event: Event): void {
        const select = event.target as HTMLSelectElement;
        const nextPageSize = Number(select.value);

        if (!Number.isFinite(nextPageSize) || nextPageSize <= 0 || nextPageSize === this.pageSize) {
            return;
        }

        this.emitPage(0, nextPageSize);
    }

    private emitPage(pageIndex: number, pageSize: number): void {
        const previousPageIndex = this.pageIndex;

        this.page.emit({
            pageIndex,
            pageSize,
            length: this.length,
            previousPageIndex,
        });
    }
}
